/**
 * Advanced password validation utilities
 * Includes common pattern detection and security checks
 */

interface PasswordValidationResult {
  isValid: boolean;
  score: number; // 0-100
  issues: string[];
  suggestions: string[];
}

const COMMON_WEAK_PATTERNS = [
  /^password/i,
  /^123456/,
  /^qwerty/i,
  /^abc123/i,
  /^admin/i,
  /^user/i,
  /^login/i,
  /^welcome/i,
  /^letmein/i,
  /^monkey/i,
  /^dragon/i,
  /^passw0rd/i,
  /^p@ssword/i,
  /^p@ssw0rd/i
];

const SEQUENTIAL_PATTERNS = [
  /012|123|234|345|456|567|678|789|890/,
  /abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz/i,
  /qwe|rty|tyu|yui|uio|iop/i,
  /asd|sdf|dfg|fgh|ghj|hjk|jkl|zxc|xcv|cvb|vbn|bnm/i
];

/**
 * Check for common weak password patterns
 */
export function detectWeakPatterns(password: string): string[] {
  const issues: string[] = [];

  // Check common weak patterns
  for (const pattern of COMMON_WEAK_PATTERNS) {
    if (pattern.test(password)) {
      issues.push('Contains common weak words or phrases');
      break;
    }
  }

  // Check sequential patterns
  for (const pattern of SEQUENTIAL_PATTERNS) {
    if (pattern.test(password)) {
      issues.push('Contains sequential characters (e.g., 123, abc, qwe)');
      break;
    }
  }

  // Check for repeated characters
  if (/(.)\1{2,}/.test(password)) {
    issues.push('Contains repeated characters (e.g., aaa, 111)');
  }

  // Check for simple keyboard patterns
  if (/^[qwertyuiop]+$/i.test(password) || /^[asdfghjkl]+$/i.test(password) || /^[zxcvbnm]+$/i.test(password)) {
    issues.push('Contains simple keyboard patterns');
  }

  // Check for date patterns
  if (/\b(19|20)\d{2}\b/.test(password) || /\b(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{2,4}\b/.test(password)) {
    issues.push('Contains date patterns');
  }

  return issues;
}

/**
 * Calculate password entropy (basic approximation)
 */
export function calculateEntropy(password: string): number {
  let charset = 0;

  if (/[a-z]/.test(password)) charset += 26;
  if (/[A-Z]/.test(password)) charset += 26;
  if (/\d/.test(password)) charset += 10;
  if (/[^a-zA-Z\d]/.test(password)) charset += 32; // Special characters

  if (charset === 0) return 0;

  return Math.log2(Math.pow(charset, password.length));
}

/**
 * Comprehensive password validation
 */
export function validatePasswordStrength(password: string): PasswordValidationResult {
  const issues: string[] = [];
  const suggestions: string[] = [];
  let score = 0;

  // Basic requirements
  const hasMinLength = password.length >= 8;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  // Calculate base score from requirements
  const requirements = [hasMinLength, hasLowercase, hasUppercase, hasNumbers, hasSpecialChars];
  const metRequirements = requirements.filter(Boolean).length;
  score += (metRequirements / 5) * 60; // 60% of score from requirements

  // Length bonus
  if (password.length >= 12) score += 10;
  else if (password.length >= 10) score += 5;

  // Entropy bonus (up to 20 points)
  const entropy = calculateEntropy(password);
  const entropyScore = Math.min(20, (entropy / 100) * 20);
  score += entropyScore;

  // Detect weak patterns (can reduce score significantly)
  const weakPatternIssues = detectWeakPatterns(password);
  if (weakPatternIssues.length > 0) {
    score = Math.max(0, score - 30); // Reduce score for weak patterns
    issues.push(...weakPatternIssues);
  }

  // Generate suggestions
  if (!hasMinLength) {
    suggestions.push('Use at least 8 characters');
  }
  if (!hasLowercase) {
    suggestions.push('Include lowercase letters');
  }
  if (!hasUppercase) {
    suggestions.push('Include uppercase letters');
  }
  if (!hasNumbers) {
    suggestions.push('Include numbers');
  }
  if (!hasSpecialChars) {
    suggestions.push('Include special characters (!@#$%^&*)');
  }
  if (weakPatternIssues.length > 0) {
    suggestions.push('Avoid common words, sequences, or patterns');
  }
  if (password.length < 12) {
    suggestions.push('Consider using 12 or more characters');
  }

  // Ensure score is between 0-100
  score = Math.max(0, Math.min(100, Math.round(score)));

  return {
    isValid: metRequirements >= 3 && issues.length === 0, // At least 3 requirements + no weak patterns
    score,
    issues,
    suggestions
  };
}

/**
 * Get password strength label and color
 */
export function getPasswordStrengthLabel(score: number): { label: string; color: string } {
  if (score >= 80) return { label: 'Very Strong', color: 'success' };
  if (score >= 60) return { label: 'Strong', color: 'success' };
  if (score >= 40) return { label: 'Fair', color: 'warning' };
  if (score >= 20) return { label: 'Weak', color: 'warning' };
  return { label: 'Very Weak', color: 'error' };
}